import { useEffect } from 'react';
import reportFrontendIncident from './reportFrontendIncident';
import isIgnorableRealtimeDisconnectError from './isIgnorableRealtimeDisconnectError';

function getErrorMessage(errorLike) {
	if (!errorLike) {
		return 'Unknown frontend error';
	}

	if (typeof errorLike === 'string') {
		return errorLike;
	}

	if (typeof errorLike.message === 'string' && errorLike.message) {
		return errorLike.message;
	}

	try {
		return JSON.stringify(errorLike);
	} catch (_) {
		return String(errorLike);
	}
}

function getErrorStack(errorLike) {
	if (!errorLike || typeof errorLike !== 'object') {
		return null;
	}

	return typeof errorLike.stack === 'string' ? errorLike.stack : null;
}

function getResourceUrl(target) {
	if (!target || typeof target !== 'object') {
		return null;
	}

	return target?.src || target?.href || null;
}

export default function useGlobalFrontendMonitoring(context) {
	useEffect(() => {
		if (typeof window === 'undefined') {
			return undefined;
		}

		const reportWithContext = (payload) =>
			reportFrontendIncident({
				...context,
				...payload,
				metadata: {
					...(context?.metadata || {}),
					...(payload.metadata || {}),
				},
			});

		const handleError = (event) => {
			if (isIgnorableRealtimeDisconnectError(event.error || event.message)) {
				return;
			}

			const resourceUrl = getResourceUrl(event.target);
			const isResourceError = !!resourceUrl && !event.error;

			reportWithContext({
				type: isResourceError ? 'resource-load-error' : 'window-error',
				severity: 'error',
				message: getErrorMessage(event.error || event.message || 'Unhandled window error'),
				stack: getErrorStack(event.error),
				module_name: context?.module_name || null,
				metadata: {
					filename: event.filename || null,
					lineno: event.lineno || null,
					colno: event.colno || null,
					resource_url: resourceUrl,
				},
			});
		};

		const handleUnhandledRejection = (event) => {
			const reason = event.reason;
			if (isIgnorableRealtimeDisconnectError(reason)) {
				return;
			}

			reportWithContext({
				type: 'unhandled-rejection',
				severity: 'error',
				message: getErrorMessage(reason),
				stack: getErrorStack(reason),
				module_name: context?.module_name || null,
				metadata: {
					reason_type: reason?.name || typeof reason,
				},
			});
		};

		window.addEventListener('error', handleError, true);
		window.addEventListener('unhandledrejection', handleUnhandledRejection);

		return () => {
			window.removeEventListener('error', handleError, true);
			window.removeEventListener('unhandledrejection', handleUnhandledRejection);
		};
	}, [
		context?.module_name,
		context?.pharmacy_id,
		context?.pharmacy_name,
		context?.pharmacy_user_id,
		context?.route_path,
	]);
}
